import { LocaleValue } from "@eres/core/dist/LocaleValue";

import _ from "lodash";
import { UserNotification } from "@10x/sso";
import { StringUtil } from "@eres/core";

interface SSOError extends LocaleValue {
	code: string;
	desc?: string;
	src?: string;
}

export interface SSOResponse<T> {
	data: T;
	error?: SSOError;
}

export interface ProfileResponse<T> {
	response: T;
	error?: SSOError;
}


export interface SSOLoginData {
	token: string;
	email?: string;
	mobileNumber?: string;
	passCode: "NEW" | "OLD";
}

export interface SSOLoginVerifyData {
	token: string;
	email?: string;
	mobileNumber?: string;
}

export type AllowedLoginMethod = "USERID_PASSWORD" | "UAEPASS" | "EID_PASSCODE";

export interface Application {
	applicationId: string;
	sso: string;
	userAccountId: string;
	policies?: {
		allowedLoginMethods: AllowedLoginMethod[];
	};
}

export interface EIDAProfileBase {
	fullNameEn?: string;
	fullNameAr?: string;
	fNameAr?: string;
	fNameEn?: string;
	lNameAr?: string;
	lNameEn?: string;
	mNameAr?: string;
	mNameEn?: string;
}

interface OtpContainer {
	key?: string;
	createdAt?: number;
	status?: "NEW" | "OLD" | "VERIFIED",
}

export interface EIDAServerProfile extends EIDAProfileBase {
	email: OtpContainer;
	mobileNumber: OtpContainer;
	emiratesId: OtpContainer;
}

export interface EIDAClientProfile extends EIDAProfileBase {
	email: string;
	mobileNumber?: string;
	emiratesId: string;
	sp: {
		email: OtpContainer;
		mobileNumber?: OtpContainer;
		emiratesId: OtpContainer;
	};
}

export interface Profile {
	applications?: Application[];
	email?: string;
	emiratesId: string;
	fullName?: string;
	fullNameAr?: string;
	mobileNumber?: string;
	userAccountIDs?: string[];
	notifications?: UserNotification[];
	selectedUserAccountId?: string;
	loginMethod?: string;
	emailVerifiedOn?: string;
	lastPassChange?: string;
}

export interface ApplicationProfiles {
	profiles: ApplicationProfile[];
}

export interface ApplicationProfile {
	userName: string;
	userAccountId: number;
	avatarUrl: string;
	additionalInfo: AdditionalInfo[];
	additionalData: any;
	userTypeId: number;
}

export interface AdditionalInfo {
	key: NameCulture;
	value: NameCulture;
}

export interface NameCulture {
	englishName: string;
	arabicName: string;
}

export function convertToEidaClientProfile( profile: EIDAServerProfile ): EIDAClientProfile {
	const cp: Partial<EIDAClientProfile> = _.omit( profile, [ "email", "mobileNumber", "emiratesId" ] );
	cp.email = profile.email.key;
	cp.emiratesId = profile.emiratesId.key;
	cp.mobileNumber = profile.mobileNumber.key;
	cp.sp = _.pick( profile, [ "email", "mobileNumber", "emiratesId" ] );
	return cp as EIDAClientProfile;
}

export function convertToEidaServerProfile( profile: EIDAClientProfile ): EIDAServerProfile {
	const sp: Partial<EIDAServerProfile> = _.omit( profile, [ "email", "mobileNumber", "emiratesId" ] );
	sp.email = { key: profile.email };
	sp.emiratesId = { key: profile.emiratesId };
	sp.mobileNumber = { key: profile.mobileNumber };
	if ( profile.sp ) {
		sp.email = {
			...profile.sp.email,
			key: sp.email.key,
		};
		sp.emiratesId = {
			...profile.sp.emiratesId,
			key: sp.emiratesId.key,
		};
		sp.mobileNumber = {
			...profile.sp.mobileNumber,
			key: sp.mobileNumber.key,
		};
	}
	return sp as EIDAServerProfile;
}

export interface ProfileUpdateRequest {
	email?: string;
	mobile?: string;
}

export type ReqStatus = "Fail" | "Success";

export interface ProfileUpdateResponse {
	email?: OtpContainer;
	mobileNumber?: OtpContainer;
	emiratesId?: OtpContainer;
}

export type CorrectionTak = ( data: any ) => any;


export function canAccess( app: {
	id: string;
	sso?: "ejari" | "tabu";
}, apps: Application[] ): boolean {
	for ( const application of apps ) {
		if ( app.id + "" === application.applicationId + "" ) {
			if ( !StringUtil.isEmptyStringOrNull( app.sso ) ) {
				if ( app.sso === application.sso ) {
					return true;
				}
			} else {
				return true;
			}
		}
	}
	return false;
}
